import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from 'react';
import classes from './PasswordlessLogin.module.css';
import cx from 'clsx';
import { isNotEmpty, TransformedValues, useForm } from '@mantine/form';
import { useCognitoAction, useCognitoSession } from '@/contexts/Cognito';
import { useRouter } from 'next/router';
import { presentUsPhoneNumber } from '@/lib/formatters/telephone';
import { AuthError, confirmSignIn, signIn } from 'aws-amplify/auth';
import InfoBanner from '@/components/InfoBanner';
import StepHeader from '@/components/Enrollment/StepHeader';
import PinCodeInput from '@/components/PinCodeInput';
import { Button, TextInput } from '@mantine/core';
import Link from 'next/link';
import LinkButton from '@/components/mantinenext/LinkButton';
import { isEmailOrPhoneNumber } from '@/lib/validators';
import EmailOrPhoneInput from '@/components/EmailOrPhoneInput';
import { useMediaQuery } from '@mantine/hooks';
import { useTenantConfig } from '@/contexts/TenantConfig';
import { useFeatureFlag } from '@/contexts/FeatureFlag';

export interface PasswordlessLoginProps {}

type CognitoSignInStep =
  | Awaited<ReturnType<typeof signIn>>['nextStep']
  | Awaited<ReturnType<typeof confirmSignIn>>['nextStep'];

type CognitoSignInOptions = Parameters<typeof signIn>[0]['options'];

const PASSCODE_LENGTH = 8;

/**
 * Performs Cognito login with email or phone number and confirmation code.
 *
 * Implements the relevant flow states described in the AWS Amplify documentation:
 * https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/sign-in/
 */
const PasswordlessLogin: FunctionComponent<PasswordlessLoginProps> = () => {
  const loginDisabled = useFeatureFlag('disableConventionalAuthentication');

  const narrowScreen = useMediaQuery('(max-width: 580px)');
  const { push, reload } = useRouter();
  const { authenticated, error: sessionError } = useCognitoSession();
  const { redirectAfterLogin } = useCognitoAction();

  const { name, slug } = useTenantConfig();
  const isNorthwellDirect = slug === 'northwell-direct';

  useEffect(() => {
    if (authenticated) {
      // When flow is complete, call the redirect handler that will restore the user to their
      // previous requested location if set, or the default location if not.
      redirectAfterLogin();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps
       -- don't include redirectAfterLogin in the deps, as it is not a stable reference. */
  }, [authenticated]);

  const [nextFlowStep, setNextFlowStep] = useState<CognitoSignInStep | null>(
    null
  );
  const [didResendCode, setDidResendCode] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);

  const form = useForm({
    // Form is extremely small, so we enabled controlled mode for state simplicity.
    mode: 'controlled',
    initialValues: {
      emailOrPhone: '',
      code: '',
    },
    validate: {
      emailOrPhone: (value: string) =>
        isEmailOrPhoneNumber('Must be a valid email or phone number')(value) ??
        isNotEmpty('Must be a valid email or phone number')(value),

      // Code is not required at the first step, but is required thereafter.
      code: nextFlowStep
        ? isNotEmpty('Verification code is required')
        : undefined,
    },
    validateInputOnBlur: true,
  });

  const submitUserIdentifier = async (
    values: TransformedValues<typeof form>
  ) => {
    // Sniff to see if username is phone or email:
    const isPhoneInput = values.emailOrPhone.startsWith('+');

    const options: CognitoSignInOptions = isPhoneInput
      ? {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'SMS_OTP',
        }
      : {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        };

    setAuthError(null);
    try {
      const { nextStep } = await signIn({
        username: values.emailOrPhone,
        options,
      });

      setNextFlowStep(nextStep);
    } catch (error) {
      if (error instanceof AuthError) {
        setAuthError(error);
      } else {
        setAuthError(
          new Error(
            'An unknown auth error occurred while trying to initiate sign in'
          )
        );
      }
    }
  };

  const challengeResponse = async (values: TransformedValues<typeof form>) => {
    setDidResendCode(false);
    setAuthError(null);
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: values.code,
      });
      setNextFlowStep(nextStep);
    } catch (error) {
      if (error instanceof AuthError) {
        setAuthError(error);
      } else {
        setAuthError(
          new Error(
            'An unknown auth error occurred while trying to confirm sign in'
          )
        );
      }
    }
  };

  // There is no `resendSignInCode` in the way there is `resendSignUpCode`, so we just repeat the
  // original login request.
  const resendCode = async () => {
    setAuthError(null);
    form.setFieldValue('code', '');
    await submitUserIdentifier(form.getTransformedValues());
    setDidResendCode(true);
  };

  // Render errors, either as a banner or as an inline error associated with the input.
  const [errorBanner, inputError] = useMemo(() => {
    if (authError?.name === 'UserNotFoundException') {
      // Sniff to see if username is phone or email:
      const isPhoneInput = form.values.emailOrPhone.startsWith('+');
      const inputError = isPhoneInput ? (
        <Fragment key="auth-error-inline">
          This mobile number isn&apos;t recognized. Please check your entry or
          try signing in with your email. If you don&apos;t have an account, you
          can <Link href="/enroll">sign up</Link>.
        </Fragment>
      ) : (
        <Fragment key="auth-error-inline">
          This email isn&apos;t recognized. Please check your entry or try
          signing in with your phone number. If you don&apos;t have an account,
          you can <Link href="/enroll">sign up</Link>.
        </Fragment>
      );
      return [null, inputError];
    } else if (authError?.name === 'CodeMismatchException') {
      // user entered the wrong code:
      const inputError = (
        <Fragment key="auth-error-inline">
          Incorrect verification code. Please double check your entry or request
          a new code.
        </Fragment>
      );
      return [null, inputError];
    } else if (
      authError?.name === 'NotAuthorizedException' ||
      authError?.name === 'ExpiredCodeException'
    ) {
      // code expired
      const inputError = (
        <Fragment key="auth-error-inline">
          Verification code has expired. Please request a new code and try
          again.
        </Fragment>
      );
      return [null, inputError];
    } else if (authError) {
      return [
        <InfoBanner
          key="auth-error-banner"
          meaning="negative"
          title="Sign In Error"
          className={classes.banner}>
          {authError.message}
        </InfoBanner>,
        null,
      ];
    } else {
      return [];
    }
  }, [authError, form.values.emailOrPhone]);

  if (loginDisabled) {
    return (
      <div className={cx(classes.root)}>
        <StepHeader title="Cannot Authenticate Here">
          <p>{name} does not allow logging in directly through this portal.</p>
        </StepHeader>

        <InfoBanner meaning="info" title="Access Instructions">
          <p>
            You may access {name} features via links you receive in email or
            SMS, or by logging in directly from your provider&apos;s portal if
            available.
          </p>
        </InfoBanner>
      </div>
    );
  }

  if (!nextFlowStep) {
    return (
      <form
        className={cx(classes.root)}
        onSubmit={form.onSubmit(
          (values) => submitUserIdentifier(values),
          (errors) => {
            const firstErrorPath = Object.keys(errors)[0];
            form.getInputNode(firstErrorPath)?.focus();
          }
        )}>
        <StepHeader title="Welcome!">
          {isNorthwellDirect
            ? 'If you are a Northwell Direct member with IBA, please enter your mobile number or email to log in.'
            : 'Enter your mobile number or email to log in.'}
        </StepHeader>

        {/* Only display Session Expired notice on the first screen: */}
        {sessionError && (
          <InfoBanner
            meaning="neutral"
            title="Session Ended"
            className={classes.banner}>
            {sessionError.message}
          </InfoBanner>
        )}
        {errorBanner}

        <div className={classes.fields}>
          <EmailOrPhoneInput
            label="Email or Phone"
            {...form.getInputProps('emailOrPhone')}
            error={inputError ?? form.getInputProps('emailOrPhone').error}
          />
        </div>

        <footer className={classes.footer}>
          <div className={classes.footerContent}>
            <Button fullWidth type="submit">
              Continue
            </Button>
            <Button fullWidth variant="outline" onClick={() => push('/enroll')}>
              Don&apos;t have an account? Sign Up
            </Button>
          </div>
        </footer>
      </form>
    );
  }

  // Handle any PIN code challenge.
  if (
    nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
    nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
    nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
    nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
  ) {
    const { deliveryMedium } =
      nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
      nextFlowStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
        ? (nextFlowStep.codeDeliveryDetails ?? {})
        : {};

    return (
      <form
        className={cx(classes.root)}
        onSubmit={form.onSubmit((values) => challengeResponse(values))}>
        {deliveryMedium === 'SMS' ? (
          <StepHeader title="Phone number verification">
            <p>
              Please enter the one-time verification code we sent to{' '}
              <strong>{presentUsPhoneNumber(form.values.emailOrPhone)}</strong>.
            </p>
          </StepHeader>
        ) : deliveryMedium === 'EMAIL' ? (
          <StepHeader title="Email verification">
            <p>
              Please enter the one-time verification code we sent to{' '}
              <strong>{form.values.emailOrPhone}</strong>.
            </p>
          </StepHeader>
        ) : (
          // This unknown handler is unexpected but ensures we don't get a total failure if
          // we use a different delivery medium unexpectedly.
          <StepHeader title="Account verification">
            Please enter the one-time verification code.
          </StepHeader>
        )}

        {errorBanner}

        <div className={classes.fields}>
          {didResendCode && <InfoBanner meaning="info" title="Code Resent" />}

          {narrowScreen ? (
            <TextInput
              className={classes.codeInput}
              inputMode="numeric"
              maxLength={PASSCODE_LENGTH}
              placeholder=""
              aria-label="Verification code"
              {...form.getInputProps('code')}
              error={inputError ?? form.getInputProps('code').error}
            />
          ) : (
            <PinCodeInput
              className={classes.codeInput}
              type="number"
              oneTimeCode
              length={PASSCODE_LENGTH}
              gap="12px"
              placeholder=""
              size={narrowScreen ? 'md' : 'lg'}
              ariaLabel="Verification code"
              {...form.getInputProps('code')}
              error={inputError ?? form.getInputProps('code').error}
            />
          )}

          <Button
            className={classes.resendButton}
            variant="linkButton"
            onClick={resendCode}>
            Resend Code
          </Button>
        </div>

        <footer className={classes.footer}>
          <div className={classes.footerContent}>
            <Button fullWidth type="submit">
              Continue
            </Button>
          </div>
        </footer>
      </form>
    );
  }

  if (nextFlowStep.signInStep === 'DONE') {
    return (
      <div className={cx(classes.root)}>
        <StepHeader title="Signed in">
          <p>Redirecting…</p>
        </StepHeader>
        <footer className={classes.footer}>
          <div className={classes.footerContent}>
            <LinkButton href="/" fullWidth>
              Continue
            </LinkButton>
          </div>
        </footer>
      </div>
    );
  }

  // Unsupported nextStep:
  return (
    <div className={cx(classes.root)}>
      <StepHeader title="Sign in error">
        <p>
          We were unable to sign you in as the server made an unexpected
          response.
        </p>
      </StepHeader>

      <InfoBanner meaning="negative" title="Unexpected Cognito Step">
        <p>
          The authentication provider has responded with an unsupported
          authentication step. Please contact support for assistance.
        </p>
        <p>
          <code>{nextFlowStep.signInStep}</code>
        </p>
      </InfoBanner>

      <footer className={classes.footer}>
        <div className={classes.footerContent}>
          <Button onClick={reload} fullWidth>
            Restart
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default PasswordlessLogin;
